import type {FC} from 'react';
import {useEffect, useState} from 'react';
import {Box, Select, Stack} from '@wellstone-solutions/web';
import {BridgeEventSources, Events} from '@wellstone-solutions/common';
import {useEventSource} from 'hooks/useEventSource';
import {useStores} from 'hooks/useStores';
import {ActionButton, PageHeader} from 'components/PageHeader';
import {ConversationList} from './ConversationList';
import PfChatModule from 'components/chat/PfChatModule';
import {ConversationHeader} from './ConversationHeader';
import {StartConversationDialog} from './StartConversationDialog';
import type {Channel, ChannelCollection} from '../types';

export const MyConversations: FC = () => {
  const {appUIStore, channelStore, eventStore, organizationStore} = useStores();
  useEventSource(BridgeEventSources.CONVERSATIONS_PAGE);

  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [channelCollection, setChannelCollection] =
    useState<ChannelCollection>('all');
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);

  const handleStartConversation = (channel: Channel) => {
    setSelectedChannel(channel);
    setIsStartDialogOpen(false);
  };

  useEffect(() => {
    appUIStore.setBreadcrumbs([]);
    eventStore.addEvent(Events.VIEWED_CONVERSATIONS);
  }, []);

  return (
    <Box>
      <PageHeader
        title="My Conversations"
        description="Stay in touch with staff and members."
      />

      <StartConversationDialog
        open={isStartDialogOpen}
        onClose={() => setIsStartDialogOpen(false)}
        onSubmit={handleStartConversation}
      />

      <Stack
        direction={{xs: 'column', md: 'row'}}
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={4}>
        <Box flex={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{marginBottom: 2}}>
            <Select
              label="View"
              value={channelCollection}
              onChange={(e) => {
                setChannelCollection(e.target.value);
                setSelectedChannel(null);
                channelStore.loadChannels(
                  true,
                  e.target.value === 'unread',
                  organizationStore.organizationId,
                );
              }}
              items={[
                {label: 'All Conversations', value: 'all'},
                {label: 'Unread Conversations', value: 'unread'},
              ]}
              canUnselect={false}
              containerProps={{
                size: 'small',
                sx: {
                  minWidth: 225,
                },
              }}
            />

            <ActionButton
              label="New Conversation"
              onClick={() => setIsStartDialogOpen(true)}
              testId="new-conversation-button"
            />
          </Stack>

          <ConversationList
            selectedChannel={selectedChannel}
            onSelect={setSelectedChannel}
            channelCollection={channelCollection}
          />
        </Box>

        <Box flex={1}>
          {selectedChannel && (
            <>
              <ConversationHeader channel={selectedChannel} />
              <PfChatModule channel={selectedChannel.id} />
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
